import React, { useState, useEffect, useCallback } from 'react';
import { 
  Grid, 
  Card, 
  CardContent, 
  Typography, 
  Box, 
  Paper, 
  LinearProgress,
  Chip
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Pie, Bar } from 'react-chartjs-2';
import { formatCurrency } from '../utils/format';
import { Chart as ChartJS, ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title } from 'chart.js';
import { ensureLogin, fetchProjects } from '../utils/api';

// Registrar componentes do Chart.js
ChartJS.register(ArcElement, Tooltip, Legend, CategoryScale, LinearScale, BarElement, Title);

// Removidos mocks: vamos usar dados reais da API

const Dashboard = () => {
  const theme = useTheme();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState({
    totalProjects: 0,
    activeProjects: 0,
    completedProjects: 0,
    totalBudget: 0
  });

  const toBudgetNumber = (v) => {
    if (v === null || v === undefined || v === '') return 0;
    const s = String(v).trim().replace(',', '.');
    const n = parseFloat(s);
    return Number.isFinite(n) ? n : 0;
  };

  const refresh = useCallback(async () => {
    await ensureLogin();
    const proj = await fetchProjects();
    setProjects(proj);
    setStats({
      totalProjects: proj.length,
      activeProjects: proj.filter(p => p.status !== 'completed' && p.status !== 'cancelled').length,
      completedProjects: proj.filter(p => p.status === 'completed').length,
      totalBudget: proj.reduce((sum, p) => sum + toBudgetNumber(p.budget), 0)
    });
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
    const onFocus = () => refresh();
    const onVisibility = () => { if (!document.hidden) refresh(); };
    window.addEventListener('focus', onFocus);
    document.addEventListener('visibilitychange', onVisibility);
    const iv = setInterval(refresh, 30000);
    return () => {
      window.removeEventListener('focus', onFocus);
      document.removeEventListener('visibilitychange', onVisibility);
      clearInterval(iv);
    };
  }, [refresh]);

  // Dados para o gráfico de status dos projetos
  const statusData = {
    labels: ['Planeamento', 'Kick-off', 'Execução', 'Encerramento', 'Concluído'],
    datasets: [
      {
        data: [
          projects.filter(p => p.status === 'planning').length,
          projects.filter(p => p.status === 'kickoff').length,
          projects.filter(p => p.status === 'execution').length,
          projects.filter(p => p.status === 'closing').length,
          projects.filter(p => p.status === 'completed').length,
        ],
        backgroundColor: [
          '#FFCE56',
          '#36A2EB',
          '#FF6384',
          '#4BC0C0',
          '#9966FF',
        ],
      },
    ],
  };

  // Dados para o gráfico de orçamento por projeto
  const budgetData = {
    labels: projects.map(p => p.title.substring(0, 15) + (p.title.length > 15 ? '...' : '')),
    datasets: [
      {
        label: 'Orçamento (€)',
        data: projects.map(p => toBudgetNumber(p.budget)),
        backgroundColor: 'rgba(54, 162, 235, 0.6)',
      },
    ],
  };

  // Opções para o gráfico de barras
  const textColor = theme.palette.text.primary;
  const gridColor = theme.palette.divider;
  const barOptions = {
    responsive: true,
    plugins: {
      legend: {
        position: 'top',
        labels: { color: textColor },
      },
      title: {
        display: false,
        text: 'Orçamento por Projeto',
        color: textColor,
      },
    },
    scales: {
      x: {
        ticks: { color: textColor },
        grid: { color: gridColor },
      },
      y: {
        ticks: { color: textColor },
        grid: { color: gridColor },
      },
    },
  };

  const pieOptions = {
    plugins: {
      legend: { labels: { color: textColor } },
      title: { display: false, text: 'Status dos Projetos', color: textColor },
    },
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'planning': return '#FFCE56';
      case 'kickoff': return '#36A2EB';
      case 'execution': return '#FF6384';
      case 'closing': return '#4BC0C0';
      case 'completed': return '#9966FF';
      default: return '#999999';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'planning': return 'Planeamento';
      case 'kickoff': return 'Kick-off';
      case 'execution': return 'Execução';
      case 'closing': return 'Encerramento';
      case 'completed': return 'Concluído';
      default: return 'Desconhecido';
    }
  };

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
        <Typography variant="h4" gutterBottom>
          Dashboard
        </Typography>
        <Chip label="Atualizar" onClick={refresh} clickable color="primary" />
      </Box>

      {/* Cards de estatísticas */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Total de Projetos
              </Typography>
              <Typography variant="h4">
                {stats.totalProjects}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Projetos Ativos
              </Typography>
              <Typography variant="h4">
                {stats.activeProjects}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Projetos Concluídos
              </Typography>
              <Typography variant="h4">
                {stats.completedProjects}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card>
            <CardContent>
              <Typography color="textSecondary" gutterBottom>
                Orçamento Total
              </Typography>
              <Typography variant="h4">
                {formatCurrency(stats.totalBudget)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Gráficos */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Status dos Projetos
            </Typography>
            <Box sx={{ height: 300, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
              <Pie data={statusData} options={pieOptions} />
            </Box>
          </Paper>
        </Grid>
        <Grid item xs={12} md={7}>
          <Paper sx={{ p: 2, height: '100%' }}>
            <Typography variant="h6" gutterBottom>
              Orçamento por Projeto
            </Typography>
            <Box sx={{ height: 300 }}>
              <Bar options={barOptions} data={budgetData} />
            </Box>
          </Paper>
        </Grid>
      </Grid>

      {/* Lista de projetos recentes */}
      <Paper sx={{ p: 2 }}>
        <Typography variant="h6" gutterBottom>
          Projetos Recentes
        </Typography>
        <Grid container spacing={2}>
          {projects.map((project) => (
            <Grid item xs={12} key={project.id}>
              <Paper sx={{ p: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    {project.title}
                  </Typography>
                  <Chip 
                    label={getStatusText(project.status)} 
                    size="small" 
                    sx={{ backgroundColor: getStatusColor(project.status), color: 'white' }}
                  />
                </Box>
                <Typography variant="body2" color="textSecondary" gutterBottom>
                  Cliente: {project.client}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                  <Box sx={{ width: '100%', mr: 1 }}>
                    <LinearProgress 
                      variant="determinate" 
                      value={project.completionPercentage} 
                      sx={{ height: 10, borderRadius: 5 }}
                    />
                  </Box>
                  <Box sx={{ minWidth: 35 }}>
                    <Typography variant="body2" color="textSecondary">
                      {project.completionPercentage}%
                    </Typography>
                  </Box>
                </Box>
              </Paper>
            </Grid>
          ))}
        </Grid>
      </Paper>
    </Box>
  );
};

export default Dashboard;
